import React, { useEffect, useState } from 'react';
import { Lock, Edit2, Save, X, AlertCircle, CheckCircle2, Loader2, Info } from 'lucide-react';
import api from '../../services/api';
import { useMyAnagrafica } from '../../hooks/useMyAnagrafica';
import { ProvinciaSelect } from '../../components/form/ProvinciaSelect';

export default function DatiFatturazionePage() {
  const { data: ana, loading, error, reload } = useMyAnagrafica();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [form, setForm] = useState({
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    email: '',
    telefono: '',
    cellulare: '',
    pec: '',
    sdiCode: '',
    emailSdi: '',
  });

  // Popola il form quando arrivano i dati
  useEffect(() => {
    if (ana) {
      setForm({
        indirizzo: ana.indirizzo ?? '',
        cap: ana.cap ?? '',
        citta: ana.citta ?? '',
        provincia: ana.provincia ?? '',
        email: ana.email ?? '',
        telefono: ana.telefono ?? '',
        cellulare: ana.cellulare ?? '',
        pec: ana.pec ?? '',
        sdiCode: ana.sdiCode ?? '',
        emailSdi: (ana as any).emailSdi ?? '',
      });
    }
  }, [ana]);

  const pecSdiMissing = !form.pec.trim() && !form.sdiCode.trim();
  const pecSdiError = submitAttempted && pecSdiMissing
    ? 'Indica la PEC oppure il Codice SDI: almeno uno dei due è obbligatorio.'
    : null;

  const handleEdit = () => {
    setSaveError(null);
    setSaveOk(false);
    setSubmitAttempted(false);
    setEditing(true);
  };

  const handleCancel = () => {
    // Ripristina i valori originali
    if (ana) {
      setForm({
        indirizzo: ana.indirizzo ?? '',
        cap: ana.cap ?? '',
        citta: ana.citta ?? '',
        provincia: ana.provincia ?? '',
        email: ana.email ?? '',
        telefono: ana.telefono ?? '',
        cellulare: ana.cellulare ?? '',
        pec: ana.pec ?? '',
        sdiCode: ana.sdiCode ?? '',
        emailSdi: (ana as any).emailSdi ?? '',
      });
    }
    setSaveError(null);
    setSubmitAttempted(false);
    setEditing(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    // Validazione: almeno uno tra PEC e SDI
    if (pecSdiMissing) {
      setSaveError('Almeno uno tra PEC e Codice SDI deve essere popolato.');
      /* L'errore era annunciato solo dalla nota generica sopra la sezione,
         che con la tastiera aperta è fuori schermo: si porta l'utente sul
         primo campo non valido. */
      document.querySelector<HTMLElement>('[data-testid="fat-input-pec"]')?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
      document.querySelector<HTMLInputElement>('[data-testid="fat-input-pec"]')?.focus({ preventScroll: true });
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      await api.put('/api/clienti/me/dati-fatturazione', {
        indirizzo: form.indirizzo || null,
        cap: form.cap || null,
        citta: form.citta || null,
        provincia: form.provincia || null,
        email: form.email || null,
        telefono: form.telefono || null,
        cellulare: form.cellulare || null,
        pec: form.pec || null,
        sdiCode: form.sdiCode || null,
        emailSdi: form.emailSdi || null,
      });
      setSaveOk(true);
      setEditing(false);
      reload();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? 'Errore durante il salvataggio.';
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="dati-fatturazione-page" className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dati di fatturazione</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gestisci i dati fiscali e di contatto del tuo account.
          </p>
        </div>
        {!loading && !editing && (
          <button
            onClick={handleEdit}
            data-testid="btn-modifica-fatturazione"
            className="h-9 px-4 rounded-lg border border-[hsl(210,64%,29%)] text-[hsl(210,64%,29%)] text-sm font-semibold hover:bg-[hsl(210,40%,96%)] inline-flex items-center gap-2 transition-colors"
          >
            <Edit2 className="w-4 h-4" /> Modifica
          </button>
        )}
      </div>

      {/* Info: campi bloccati */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 flex items-start gap-2.5 text-sm text-slate-700" data-testid="locked-fields-info">
        <Lock className="w-4 h-4 flex-none mt-0.5 text-slate-500" />
        <span>
          I campi <strong>Ragione Sociale</strong>, <strong>Codice cliente</strong>, <strong>Partita IVA</strong> e <strong>Codice Fiscale</strong> sono gestiti direttamente dal gestionale/amministrazione e non sono modificabili da questo portale.
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {saveOk && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="save-success">
          <CheckCircle2 className="w-4 h-4" /> Dati aggiornati correttamente.
        </div>
      )}
      {saveError && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid="save-error">
          <AlertCircle className="w-4 h-4" /> {saveError}
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border bg-white p-6 space-y-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="h-4 bg-[hsl(0,0%,94%)] rounded animate-pulse" style={{ width: `${40 + (i * 7) % 50}%` }} />
          ))}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">
          {/* Sezione: Dati bloccati */}
          <div className="rounded-xl border bg-[hsl(0,0%,98%)] p-5">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Dati fiscali (non modificabili)
            </h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <FieldReadOnly label="Ragione sociale" value={ana?.ragioneSociale} large />
              {/* Un Contatto ha il codice al primo ordine, quando nasce il Cliente. */}
              <FieldReadOnly
                label="Codice cliente"
                value={ana?.an_conto?.toString() ?? (ana?.contatto ? 'Assegnato al primo ordine' : undefined)}
                mono={ana?.an_conto != null}
              />
              <FieldReadOnly label="Partita IVA" value={ana?.partitaIva} mono />
              <FieldReadOnly label="Codice Fiscale" value={ana?.codiceFiscale} mono />
            </dl>
          </div>

          {/* Sezione: Indirizzo */}
          <div className="rounded-xl border bg-white p-5">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              Indirizzo
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FieldInput
                  label="Indirizzo"
                  value={form.indirizzo}
                  onChange={v => setForm(f => ({ ...f, indirizzo: v }))}
                  editing={editing}
                  testId="fat-input-indirizzo"
                  autoComplete="street-address"
                />
              </div>
              <FieldInput label="CAP" value={form.cap} onChange={v => setForm(f => ({ ...f, cap: v.replace(/\D/g, '') }))} editing={editing} testId="fat-input-cap" autoComplete="postal-code" inputMode="numeric" maxLength={5} />
              <FieldInput label="Città" value={form.citta} onChange={v => setForm(f => ({ ...f, citta: v }))} editing={editing} testId="fat-input-citta" autoComplete="address-level2" />
              {editing ? (
                <div>
                  <span className="block text-[10px] font-semibold uppercase tracking-wider mb-1 text-muted-foreground">Provincia</span>
                  <ProvinciaSelect value={form.provincia} onChange={v => setForm(f => ({ ...f, provincia: v }))} testid="fat-input-provincia" />
                </div>
              ) : (
                <FieldInput label="Provincia" value={form.provincia} onChange={() => {}} editing={false} testId="fat-input-provincia" />
              )}
              <FieldReadOnly label="Paese" value={ana?.paese || 'Italia'} />
            </div>
          </div>

          {/* Sezione: Contatti */}
          <div className="rounded-xl border bg-white p-5">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              Contatti
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldInput label="Email" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} editing={editing} testId="fat-input-email" autoComplete="email" inputMode="email" />
              <FieldInput label="Telefono" type="tel" value={form.telefono} onChange={v => setForm(f => ({ ...f, telefono: v }))} editing={editing} testId="fat-input-telefono" autoComplete="tel" inputMode="tel" />
              <FieldInput label="Cellulare" type="tel" value={form.cellulare} onChange={v => setForm(f => ({ ...f, cellulare: v }))} editing={editing} testId="fat-input-cellulare" autoComplete="mobile tel" inputMode="tel" />
            </div>
          </div>

          {/* Sezione: Fatturazione elettronica */}
          <div className="rounded-xl border bg-white p-5">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
              Fatturazione elettronica
            </h2>
            <p className="text-xs text-muted-foreground mb-4 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 flex-none mt-0.5" />
              Almeno uno tra PEC e Codice SDI è obbligatorio.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldInput
                label="PEC"
                type="email"
                value={form.pec}
                onChange={v => setForm(f => ({ ...f, pec: v }))}
                editing={editing}
                testId="fat-input-pec"
                autoComplete="email"
                inputMode="email"
                highlight={editing && pecSdiMissing}
                error={pecSdiError}
              />
              <FieldInput
                label="Codice SDI"
                value={form.sdiCode}
                onChange={v => setForm(f => ({ ...f, sdiCode: v }))}
                editing={editing}
                testId="fat-input-sdi"
                mono
                highlight={editing && pecSdiMissing}
                error={pecSdiError}
              />
              <FieldInput
                label="Email SDI (opzionale)"
                type="email"
                value={form.emailSdi}
                onChange={v => setForm(f => ({ ...f, emailSdi: v }))}
                editing={editing}
                testId="fat-input-email-sdi"
                autoComplete="email"
                inputMode="email"
              />
            </div>
          </div>

          {/* Pulsanti azione — sticky in basso sotto il breakpoint desktop.
              Il form è lungo 4 schermate: modificando l'indirizzo a metà
              pagina bisognava scorrere fino in fondo per salvare, senza
              alcun segnale che ci fossero modifiche non salvate. */}
          {editing && (
            <div
              className="sticky bottom-0 -mx-4 flex items-center justify-end gap-3 border-t bg-white px-4 py-3 sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:border-t-0 lg:bg-transparent lg:px-0 lg:py-0"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
              data-testid="fatturazione-action-bar"
            >
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                data-testid="btn-annulla-fatturazione"
                className="tap h-11 lg:h-10 px-5 rounded-lg border text-sm font-medium hover:bg-[hsl(0,0%,97%)] disabled:opacity-40"
              >
                <X className="w-4 h-4 inline mr-1.5" />Annulla
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="btn-salva-fatturazione"
                className="tap h-11 lg:h-10 flex-1 lg:flex-none px-6 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Salvataggio…' : 'Salva modifiche'}
              </button>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

// ── Componenti interni ─────────────────────────────────────────────────────────

function FieldReadOnly({
  label, value, mono, large,
}: { label: string; value?: string | number | null; mono?: boolean; large?: boolean }) {
  const shown = (value === undefined || value === null || value === '') ? '—' : String(value);
  return (
    <div data-testid={`fat-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <dt className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">{label}</dt>
      <dd className={`text-[#1f1f1d] ${large ? 'text-base font-semibold' : 'text-sm'} ${mono ? 'font-mono' : ''}`}>
        {shown}
      </dd>
    </div>
  );
}

function FieldInput({
  label, value, onChange, editing, testId, type = 'text', mono, maxLength, highlight,
  autoComplete, inputMode, error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  editing: boolean;
  testId?: string;
  type?: string;
  mono?: boolean;
  maxLength?: number;
  highlight?: boolean;
  autoComplete?: string;
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'search' | 'decimal' | 'none';
  /** Messaggio d'errore per questo campo, collegato via aria-describedby. */
  error?: string | null;
}) {
  /* Le label erano `<label>` senza `htmlFor` su `<input>` senza `id`, e
     l'input non era nemmeno annidato: nessuna associazione programmatica,
     quindi toccare l'etichetta non dava il focus e lo screen reader
     annunciava un campo senza nome. In tutta l'app esistevano 2 `htmlFor`,
     uno dei quali in codice morto. */
  const reactId = React.useId();
  const fieldId = testId ? `fld-${testId}` : `fld-${reactId}`;
  const errorId = `${fieldId}-error`;

  if (!editing) {
    return (
      <div data-testid={testId ? `view-${testId}` : undefined}>
        <dt className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">{label}</dt>
        <dd className={`text-sm text-[#1f1f1d] ${mono ? 'font-mono' : ''}`}>{value || '—'}</dd>
      </div>
    );
  }
  return (
    <div>
      <label
        htmlFor={fieldId}
        className={`block text-[10px] font-semibold uppercase tracking-wider mb-1 ${highlight ? 'text-rose-600' : 'text-muted-foreground'}`}
      >
        {label}
      </label>
      <input
        id={fieldId}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        maxLength={maxLength}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        data-testid={testId}
        className={`tap w-full h-11 md:h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30 ${mono ? 'font-mono' : ''} ${
          highlight || error ? 'border-rose-400 bg-rose-50' : 'border-slate-200 bg-white'
        }`}
      />
      {error && (
        <p id={errorId} className="mt-1 text-[13px] text-rose-700" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
